import { useCallback } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import * as fileService from '@/services/file.service';

/**
 * Bridge between AI-generated code and the editor.
 * Provides actions to create new files from AI output
 * and replace the current file's content.
 */
export function useAiEditorBridge() {
  const { openFile, updateFileContent, tabs, activeTabId, openFiles } = useEditorStore();
  const { activeWorkspace } = useWorkspaceStore();
  const workspaceId = activeWorkspace?.id ?? '';

  /**
   * Create a new file from AI-generated code and open it in the editor.
   */
  const createFileFromAi = useCallback(
    async (code: string, suggestedPath?: string) => {
      const path = normalizeAiTargetPath(code, suggestedPath);

      if (workspaceId) {
        try {
          await fileService.createFile(workspaceId, path, 'file');
          await fileService.saveFile(workspaceId, path, code);
        } catch {
          // File might already exist — still open it in the editor
        }
      }

      openFile(path, code);
      return path;
    },
    [workspaceId, openFile],
  );

  /**
   * Replace the active file's entire content with AI-generated code.
   */
  const replaceActiveFile = useCallback(
    (code: string) => {
      const tab = tabs.find((t) => t.id === activeTabId);
      if (!tab) return false;
      updateFileContent(tab.path, code);
      return true;
    },
    [tabs, activeTabId, updateFileContent],
  );

  /**
   * Insert code at the end of the active file.
   */
  const appendToActiveFile = useCallback(
    (code: string) => {
      const tab = tabs.find((t) => t.id === activeTabId);
      if (!tab) return false;
      const existing = openFiles.get(tab.path) ?? '';
      updateFileContent(tab.path, existing + '\n\n' + code);
      return true;
    },
    [tabs, activeTabId, openFiles, updateFileContent],
  );

  return { createFileFromAi, replaceActiveFile, appendToActiveFile };
}

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

/** Infer a reasonable filename from Solidity code */
function inferFileName(code: string): string {
  // Try to extract contract name
  const contractMatch = code.match(/contract\s+(\w+)/);
  if (contractMatch) return `${contractMatch[1]}.sol`;

  // Try interface
  const interfaceMatch = code.match(/interface\s+(\w+)/);
  if (interfaceMatch) return `${interfaceMatch[1]}.sol`;

  // Try library
  const libraryMatch = code.match(/library\s+(\w+)/);
  if (libraryMatch) return `${libraryMatch[1]}.sol`;

  return `Contract_${Date.now().toString(36)}.sol`;
}

function normalizeAiTargetPath(code: string, suggestedPath?: string): string {
  if (suggestedPath) {
    if (suggestedPath.includes('/')) return suggestedPath;
    return `contracts/${suggestedPath}`;
  }

  return `contracts/${inferFileName(code)}`;
}
